import { Fragment, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, TrayArrowDown, X, CheckCircle, XCircle, Warning, ArrowRight, Printer,
  ShoppingCart,
} from "@phosphor-icons/react";
import StockPartCombobox from "@/components/StockPartCombobox";
import LockStatusBanner from "@/components/LockStatusBanner";
import AgingPill from "@/components/AgingPill";
import SerialEditor from "@/components/SerialEditor";
import SiteAutocomplete from "@/components/SiteAutocomplete";
import { fmtDateTime } from "@/lib/dates";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: "bg-neutral-200 text-neutral-800",
  verified: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-900",
  partial: "bg-indigo-100 text-indigo-900",
  rejected: "bg-red-100 text-red-900",
};

export default function RequestsPage() {
  const { user, can } = useAuth();
  const canCreate = can("create_request");
  const canApprove = can("approve_request");
  const canVerify = ["supervisor", "manager", "admin"].includes(user?.role);
  const canAdminEdit = user?.role === "admin";
  const canWalkin = ["manager", "supervisor"].includes(user?.role);

  const [requests, setRequests] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [customers, setCustomers] = useState([]);           // main (billed) customers only — for REQs
  const [walkinCustomers, setWalkinCustomers] = useState([]); // walk-in customers only — for cash sales
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterEngineer, setFilterEngineer] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [showWalkin, setShowWalkin] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Server-side pagination — matches the Records / Audit pattern.
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Only these roles get to filter across other technicians' requests. Engineers
  // are always scoped to themselves by the backend, and Finance doesn't need it.
  const canFilterByEngineer = ["admin", "manager", "supervisor", "storekeeper"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, size: PAGE_SIZE };
      if (filterStatus !== "all") params.status = filterStatus;
      if (canFilterByEngineer && filterEngineer !== "all") params.engineer_id = filterEngineer;
      const [rq, eng, reg, cust, walkinCust] = await Promise.all([
        api.get("/requests", { params }),
        user.role !== "engineer" ? api.get("/engineers") : Promise.resolve({ data: [] }),
        api.get("/regions"),
        // Main REQ flow only lists MAIN (billed) customers so walk-in accounts
        // don't clutter the picker or the recent-favourites list.
        api.get("/customers", { params: { active_only: true, type: "main" } }).catch(() => ({ data: [] })),
        // Walk-in dialog only sees walk-in-typed customers — fetched separately
        // so managers/supervisors don't accidentally sell to a billed account.
        canWalkin
          ? api.get("/customers", { params: { active_only: true, type: "walkin" } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      // Backend returns `{items,total,...}` under paginated mode, array otherwise.
      const payload = rq.data;
      if (Array.isArray(payload)) {
        setRequests(payload);
        setTotal(payload.length);
      } else {
        setRequests(payload.items || []);
        setTotal(payload.total || 0);
      }
      setEngineers(eng.data || []);
      setRegions(reg.data || []);
      setCustomers(cust.data || []);
      setWalkinCustomers(walkinCust.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, filterEngineer, page]);
  // Reset to page 1 when filters change (not page itself).
  // eslint-disable-next-line
  useEffect(() => { setPage(1); }, [filterStatus, filterEngineer]);

  // Deep-link: /requests?new=1 auto-opens the Raise Request dialog.
  // Used by the engineer mobile hero button on the dashboard.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1" && canCreate) {
      setShowCreate(true);
      // strip the param so a manual close doesn't re-open on re-render
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams, canCreate]);

  const activeRequest = useMemo(
    () => requests.find((r) => r.id === decidingId) || null,
    [requests, decidingId],
  );
  const verifyingRequest = useMemo(
    () => requests.find((r) => r.id === verifyingId) || null,
    [requests, verifyingId],
  );
  const adminEditingRequest = useMemo(
    () => requests.find((r) => r.id === editingAdminId) || null,
    [requests, editingAdminId],
  );

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10 no-print">
        <div className="px-4 py-4 md:px-10 md:py-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">Helpdesk → Storekeeper</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight leading-none mt-2">Parts Requests</h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-xl hidden md:block">
              Two-step approval: Helpdesk <b>verifies</b> the REQ (grey → yellow), then Storekeeper <b>releases</b> it (yellow → green). Released lines automatically become an <b>IV</b> voucher and deduct physical stock.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canWalkin && (
              <Button
                onClick={() => setShowWalkin(true)}
                data-testid="new-walkin-btn"
                variant="outline"
                className="h-10 rounded-none border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white"
                title="Cash sale to a walk-in customer — deducts main stock immediately (Manager / Supervisor only)"
              >
                <ShoppingCart size={16} weight="bold" className="mr-2" /> Walk-in Sale
              </Button>
            )}
            {canCreate && (
              <Button
                onClick={() => setShowCreate(true)}
                data-testid="new-request-btn"
                className="h-10 rounded-none bg-neutral-900 text-neutral-50 hover:bg-neutral-800"
              >
                <Plus size={16} weight="bold" className="mr-2" /> Raise Request
              </Button>
            )}
          </div>
        </div>
        <div className="px-4 pb-4 md:px-10 flex flex-wrap items-center gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-req-status" className="h-10 w-56 rounded-none border-neutral-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending Verification</SelectItem>
              <SelectItem value="verified">Verified — Pending Release</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          {canFilterByEngineer && (
            <Select value={filterEngineer} onValueChange={setFilterEngineer}>
              <SelectTrigger data-testid="filter-req-engineer" className="h-10 w-64 rounded-none border-neutral-300">
                <SelectValue placeholder="All Technicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Technicians</SelectItem>
                {engineers.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`filter-req-engineer-${e.id}`}>
                    {e.name}{e.region ? ` · ${e.region}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canFilterByEngineer && filterEngineer !== "all" && (
            <button
              type="button"
              onClick={() => setFilterEngineer("all")}
              data-testid="filter-req-engineer-clear"
              className="h-10 px-3 border border-neutral-300 hover:border-neutral-900 text-xs font-mono uppercase tracking-wider"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-5 md:p-10">
        {/* Mobile: card list (engineers use this on phones) */}
        <div className="md:hidden space-y-3" data-testid="requests-mobile-list">
          {loading && (
            <div className="p-6 text-center text-xs text-neutral-400 border border-neutral-200 bg-white">Loading…</div>
          )}
          {!loading && requests.length === 0 && (
            <div className="p-6 text-center text-xs text-neutral-400 border border-neutral-200 bg-white">No requests yet.</div>
          )}
          {requests.map((r) => (
            <RequestCard
              key={r.id}
              r={r}
              apiBase={API_BASE}
              canVerify={canVerify}
              canApprove={canApprove}
              canAdminEdit={canAdminEdit}
              isManager={user?.role === "manager"}
              onVerify={() => setVerifyingId(r.id)}
              onDecide={() => setDecidingId(r.id)}
              onAdminEdit={() => setEditingAdminId(r.id)}
            />
          ))}
        </div>

        {/* Desktop / tablet: full table */}
        <div className="hidden md:block border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="requests-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-3 py-3 label-eyebrow">REQ #</th>
                <th className="px-3 py-3 label-eyebrow">Engineer</th>
                <th className="px-3 py-3 label-eyebrow">Region</th>
                <th className="px-3 py-3 label-eyebrow">Customer</th>
                <th className="px-3 py-3 label-eyebrow">Work Order</th>
                <th className="px-3 py-3 label-eyebrow">Items</th>
                <th className="px-3 py-3 label-eyebrow">Raised</th>
                <th className="px-3 py-3 label-eyebrow">Status</th>
                <th className="px-3 py-3 label-eyebrow">Linked IV</th>
                <th className="px-3 py-3 label-eyebrow no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-4 py-10 text-center text-neutral-400">Loading…</td></tr>}
              {!loading && requests.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-neutral-400">No requests yet.</td></tr>
              )}
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 last:border-0 align-top hover:bg-neutral-50/60" data-testid={`request-row-${r.id}`}>
                  <td className="px-3 py-3 font-mono text-xs font-medium whitespace-nowrap">{r.request_no}</td>
                  <td className="px-3 py-3">
                    <div className="font-medium">{r.for_engineer_name}</div>
                    {r.for_engineer_email && <div className="text-xs text-neutral-500 font-mono">{r.for_engineer_email}</div>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">{r.region}</td>
                  <td className="px-3 py-3 text-xs">
                    {r.customer_name
                      ? <span className="font-medium">{r.customer_name}</span>
                      : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">{r.work_order_id || <span className="text-neutral-400">—</span>}</td>
                  <td className="px-3 py-3">
                    <ul className="space-y-1">
                      {r.items.map((it, i) => (
                        <li key={i} className="text-xs flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium">{it.name}</span>
                          <span className="text-neutral-500">× {it.quantity}</span>
                          {it.chargeable && (
                            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-900" data-testid={`req-line-chargeable-${r.id}-${i}`}>
                              Chargeable
                            </span>
                          )}
                          {it.decision && (
                            <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${
                              it.decision === "approved" ? "bg-emerald-100 text-emerald-800" :
                              it.decision === "partial"  ? "bg-indigo-100 text-indigo-800" :
                                                            "bg-red-100 text-red-800"
                            }`}>
                              {it.decision}{it.approved_qty != null && it.decision !== "rejected" ? ` ${it.approved_qty}` : ""}
                            </span>
                          )}
                          {it.group && <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-neutral-100 text-neutral-700">{it.group}</span>}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">
                    {fmtDateTime(r.created_at)}
                    <div className="text-[10px] text-neutral-500 mt-0.5">by {r.requested_by_name}</div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_STYLES[r.status] || ""}`}>
                      {r.status === "pending" ? "Pending Verify" : r.status === "verified" ? "Verified · Pending Release" : r.status}
                    </span>
                    <div className="mt-1 flex items-center gap-1">
                      <AgingPill req={r} testid={`aging-${r.id}`} />
                    </div>
                    {r.verified_by_name && r.status === "verified" && (
                      <div className="text-[10px] text-neutral-500 mt-1">by {r.verified_by_name}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">
                    {r.linked_receipt_no ? (
                      <div className="flex items-center gap-2">
                        <a href={`/records?status=open`} className="underline underline-offset-2 hover:text-neutral-500 flex items-center gap-1">
                          <ArrowRight size={12} weight="bold" />{r.linked_receipt_no}
                        </a>
                        <a
                          href={`${API_BASE}/records/${r.linked_record_id}/receipt.pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white inline-flex items-center gap-1"
                          data-testid={`print-receipt-${r.id}`}
                          title="Print / download the issued IV receipt"
                        >
                          <Printer size={11} weight="bold" /> Print
                        </a>
                      </div>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-3 no-print">
                    <div className="flex flex-col gap-1 items-stretch">
                    {canVerify && r.status === "pending" ? (
                      <Button
                        size="sm"
                        onClick={() => setVerifyingId(r.id)}
                        data-testid={`verify-btn-${r.id}`}
                        className="h-8 rounded-none bg-amber-500 text-white hover:bg-amber-600"
                        title="Helpdesk verify — moves grey → yellow"
                      >
                        Verify
                      </Button>
                    ) : canApprove && r.status === "verified" ? (
                      <Button
                        size="sm"
                        onClick={() => setDecidingId(r.id)}
                        data-testid={`decide-btn-${r.id}`}
                        className="h-8 rounded-none bg-emerald-700 text-white hover:bg-emerald-800"
                        title="Storekeeper release — moves yellow → green"
                      >
                        Release
                      </Button>
                    ) : canApprove && r.status === "pending" && user?.role === "manager" ? (
                      <Button
                        size="sm"
                        onClick={() => setDecidingId(r.id)}
                        data-testid={`decide-btn-${r.id}`}
                        className="h-8 rounded-none bg-neutral-900 text-white hover:bg-neutral-800"
                        title="Manager override — skip verify and release directly"
                      >
                        Override&nbsp;→ Release
                      </Button>
                    ) : (
                      <span className="text-neutral-400 text-xs">—</span>
                    )}
                    {canAdminEdit && ["pending", "verified"].includes(r.status) && (
                      <button
                        onClick={() => setEditingAdminId(r.id)}
                        data-testid={`admin-edit-btn-${r.id}`}
                        title="Admin — edit before IV is generated"
                        className="h-7 rounded-none border border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white text-[10px] font-mono uppercase tracking-wider px-2"
                      >
                        Admin Edit
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pager — server-side pagination (25/page) */}
        <div className="px-4 md:px-10 pt-3 pb-6 flex items-center justify-between no-print" data-testid="requests-pager">
          <div className="text-xs font-mono text-neutral-500">
            {total === 0
              ? "No requests"
              : `${((page - 1) * PAGE_SIZE) + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              data-testid="requests-pager-prev"
              className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-neutral-300 bg-white hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <span className="text-[11px] font-mono text-neutral-600 tabular-nums">
              Page {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => (p * PAGE_SIZE < total ? p + 1 : p))}
              disabled={page * PAGE_SIZE >= total || loading}
              data-testid="requests-pager-next"
              className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-neutral-300 bg-white hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {canCreate && (
        <CreateRequestDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          engineers={engineers}
          regions={regions}
          customers={customers}
          currentUser={user}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {canWalkin && (
        <WalkinSaleDialog
          open={showWalkin}
          onClose={() => setShowWalkin(false)}
          regions={regions}
          customers={walkinCustomers}
          onSaved={() => { setShowWalkin(false); load(); }}
        />
      )}
      {canApprove && activeRequest && (
        <DecisionDialog
          request={activeRequest}
          currentUser={user}
          onClose={() => setDecidingId(null)}
          onSaved={() => { setDecidingId(null); load(); }}
        />
      )}
      {canVerify && verifyingRequest && (
        <VerifyDialog
          request={verifyingRequest}
          onClose={() => setVerifyingId(null)}
          onSaved={() => { setVerifyingId(null); load(); }}
        />
      )}
      {canAdminEdit && adminEditingRequest && (
        <AdminEditRequestDialog
          request={adminEditingRequest}
          customers={customers}
          onClose={() => setEditingAdminId(null)}
          onSaved={() => { setEditingAdminId(null); load(); }}
        />
      )}
    </div>
  );
}

function RequestCard({ r, apiBase, canVerify, canApprove, isManager, onVerify, onDecide }) {
  const statusLabel =
    r.status === "pending" ? "Pending Verify"
    : r.status === "verified" ? "Verified · Pending Release"
    : r.status;
  return (
    <div className="border border-neutral-200 bg-white p-4 space-y-3" data-testid={`request-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs font-semibold">{r.request_no}</div>
          <div className="text-sm font-medium mt-0.5 truncate">{r.for_engineer_name}</div>
          <div className="text-[11px] text-neutral-500 font-mono mt-0.5">{r.region}</div>
        </div>
        <div className="text-right shrink-0">
          <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider inline-block ${STATUS_STYLES[r.status] || ""}`}>
            {statusLabel}
          </span>
          <div className="mt-1 flex justify-end">
            <AgingPill req={r} testid={`aging-${r.id}`} />
          </div>
        </div>
      </div>

      {(r.customer_name || r.work_order_id) && (
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          {r.customer_name && (
            <div className="min-w-0">
              <div className="label-eyebrow">Customer</div>
              <div className="font-medium truncate">{r.customer_name}</div>
            </div>
          )}
          {r.work_order_id && (
            <div className="min-w-0">
              <div className="label-eyebrow">Work Order</div>
              <div className="font-mono truncate">{r.work_order_id}</div>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="label-eyebrow mb-1">Items</div>
        <ul className="space-y-1">
          {r.items.map((it, i) => (
            <li key={i} className="text-xs flex items-center gap-1.5 flex-wrap">
              <span className="font-medium">{it.name}</span>
              <span className="text-neutral-500">× {it.quantity}</span>
              {it.chargeable && (
                <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-900">Chargeable</span>
              )}
              {it.decision && (
                <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${
                  it.decision === "approved" ? "bg-emerald-100 text-emerald-800" :
                  it.decision === "partial"  ? "bg-indigo-100 text-indigo-800" :
                                                "bg-red-100 text-red-800"
                }`}>
                  {it.decision}{it.approved_qty != null && it.decision !== "rejected" ? ` ${it.approved_qty}` : ""}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
        <div className="text-[10px] text-neutral-500 font-mono">
          {fmtDateTime(r.created_at)}
        </div>
        {r.linked_receipt_no && (
          <a
            href={`${apiBase}/records/${r.linked_record_id}/receipt.pdf`}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-900 bg-white active:bg-neutral-900 active:text-white inline-flex items-center gap-1"
            data-testid={`print-receipt-mobile-${r.id}`}
          >
            <Printer size={11} weight="bold" /> {r.linked_receipt_no}
          </a>
        )}
      </div>

      {(canVerify && r.status === "pending") || (canApprove && r.status === "verified") || (canApprove && r.status === "pending" && isManager) ? (
        <div className="pt-2 no-print">
          {canVerify && r.status === "pending" && (
            <Button
              onClick={onVerify}
              data-testid={`mobile-verify-btn-${r.id}`}
              className="w-full h-10 rounded-none bg-amber-500 text-white hover:bg-amber-600"
            >
              Verify
            </Button>
          )}
          {canApprove && r.status === "verified" && (
            <Button
              onClick={onDecide}
              data-testid={`mobile-decide-btn-${r.id}`}
              className="w-full h-10 rounded-none bg-emerald-700 text-white hover:bg-emerald-800"
            >
              Release
            </Button>
          )}
          {canApprove && r.status === "pending" && isManager && (
            <Button
              onClick={onDecide}
              data-testid={`mobile-decide-btn-${r.id}`}
              className="w-full h-10 rounded-none bg-neutral-900 text-white hover:bg-neutral-800"
            >
              Override → Release
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}


function CreateRequestDialog({ open, onClose, engineers, regions, customers, currentUser, onSaved }) {
  const isEngineer = currentUser?.role === "engineer";
  const [engineerId, setEngineerId] = useState("");
  const [region, setRegion] = useState("");
  const [customerId, setCustomerId] = useState("__none__");
  const [workOrderId, setWorkOrderId] = useState("");
  const [site, setSite] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ name: "", quantity: 1, kind: "new", chargeable: false, notes: "", stock_code: null, group: null, uom: null }]);
  const [busy, setBusy] = useState(false);
  const [lockStatus, setLockStatus] = useState({ blocked: false });
  const [override, setOverride] = useState(false);
  const [recentCustIds, setRecentCustIds] = useState([]);
  const isManager = currentUser?.role === "manager";

  // Auto-fill the engineer + region when a technician is raising the REQ themselves.
  useEffect(() => {
    if (open && isEngineer && currentUser?.id) {
      setEngineerId(currentUser.id);
      if (currentUser.region) setRegion(currentUser.region);
    }
  }, [open, isEngineer, currentUser?.id, currentUser?.region]);

  // Fetch the 3 customers this user picked most recently so they pin at the top.
  useEffect(() => {
    if (!open) return;
    api.get("/customers/recent-for-me", { params: { limit: 3 } })
      .then((res) => setRecentCustIds((res.data?.items || []).map((it) => it.customer_id)))
      .catch(() => setRecentCustIds([]));
  }, [open]);

  const orderedCustomers = useMemo(() => {
    if (!recentCustIds.length || !customers?.length) return { recent: [], rest: customers || [] };
    const map = new Map((customers || []).map((c) => [c.id, c]));
    const recent = recentCustIds.map((id) => map.get(id)).filter(Boolean);
    const recentIds = new Set(recent.map((c) => c.id));
    const rest = (customers || []).filter((c) => !recentIds.has(c.id));
    return { recent, rest };
  }, [customers, recentCustIds]);

  const setItem = (i, k, v) => setItems(items.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const pickItem = (i, picked) => setItems(items.map((p, idx) => idx === i ? {
    ...p, name: picked.name, stock_code: picked.stock_code || null, group: picked.group || null, uom: picked.uom || null,
  } : p));
  const addItem = () => setItems([...items, { name: "", quantity: 1, kind: "new", chargeable: false, notes: "", stock_code: null, group: null, uom: null }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!engineerId) return toast.error("Select target engineer");
    if (!region) return toast.error("Select region");
    const cleaned = items
      .filter((p) => (p.name || "").trim())
      .map((p) => ({
        name: p.name.trim(),
        quantity: Math.max(1, Number(p.quantity) || 1),
        kind: p.kind === "rb" ? "rb" : "new",
        chargeable: !!p.chargeable,
        notes: p.notes || "",
        stock_code: p.stock_code || null,
        group: p.group || null,
        uom: p.uom || null,
      }));
    if (cleaned.length === 0) return toast.error("Add at least one part");
    setBusy(true);
    try {
      await api.post("/requests", {
        for_engineer_id: engineerId,
        region,
        customer_id: customerId === "__none__" ? null : customerId,
        work_order_id: workOrderId.trim() || null,
        site: site.trim() || null,
        items: cleaned,
        needed_by: neededBy ? new Date(neededBy).toISOString() : null,
        notes,
        override: isManager && override,
      });
      toast.success("Request raised");
      setEngineerId(""); setRegion(""); setCustomerId("__none__"); setWorkOrderId(""); setSite(""); setNeededBy(""); setNotes("");
      setItems([{ name: "", quantity: 1, kind: "new", chargeable: false, notes: "", stock_code: null, group: null, uom: null }]);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="create-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Raise Parts Request</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isEngineer && (
            <div className="border border-neutral-900 bg-neutral-900 text-white px-3 py-2 text-xs" data-testid="engineer-selfreq-banner">
              Raising as <b>{currentUser?.name}</b>{currentUser?.region ? ` · ${currentUser.region}` : ""}. Pick the customer this REQ is for below.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {!isEngineer && (
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Target engineer</Label>
                <Select value={engineerId} onValueChange={setEngineerId}>
                  <SelectTrigger data-testid="req-engineer" className="rounded-none border-neutral-400"><SelectValue placeholder="Select engineer" /></SelectTrigger>
                  <SelectContent>
                    {engineers.length === 0
                      ? <div className="px-2 py-3 text-xs text-neutral-500">No engineers.</div>
                      : engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Region</Label>
              <Select value={region} onValueChange={setRegion} disabled={isEngineer && !!currentUser?.region}>
                <SelectTrigger data-testid="req-region" className="rounded-none border-neutral-400"><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Customer <span className="text-neutral-400 normal-case">(optional — carries over to the issued IV for monthly consumption tracking)</span></Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="req-customer" className="rounded-none border-neutral-400"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__"><span className="text-neutral-500 italic">— No customer —</span></SelectItem>
                {orderedCustomers.recent.length > 0 && (
                  <>
                    <div className="px-2 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-neutral-500">Recent</div>
                    {orderedCustomers.recent.map((c) => (
                      <SelectItem key={`recent-${c.id}`} value={c.id} data-testid={`req-customer-recent-${c.id}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="text-amber-600">★</span> {c.name}
                        </span>
                      </SelectItem>
                    ))}
                    <div className="px-2 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-neutral-500">All customers</div>
                  </>
                )}
                {orderedCustomers.rest.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Work Order ID</Label>
              <Input value={workOrderId} onChange={(e) => setWorkOrderId(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="req-wo" placeholder="e.g. WO-2026-0421" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Site (optional)</Label>
              <SiteAutocomplete value={site} onChange={setSite} customerId={customerId} data-testid="req-site" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Needed by (optional)</Label>
            <Input type="datetime-local" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} className="rounded-none border-neutral-400" data-testid="req-needed-by" />
          </div>
          {engineerId && (
            <LockStatusBanner
              engineerId={engineerId}
              parts={items}
              isManager={isManager}
              override={override}
              onOverrideChange={setOverride}
              onStatusChange={setLockStatus}
              quotaBlocks={false}
              testidPrefix="req-lock"
            />
          )}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Requested parts</Label>
              <button type="button" onClick={addItem} data-testid="req-add-item-btn" className="text-xs font-medium underline underline-offset-4 hover:text-neutral-500">+ Add part</button>
            </div>
            <div className="hidden md:grid grid-cols-12 gap-2 px-1 text-[10px] font-mono uppercase tracking-wider text-neutral-500">
              <div className="col-span-4">Part</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2">Kind</div>
              <div className="col-span-2">Chargeable</div>
              <div className="col-span-1">Notes</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4" data-testid={`req-item-name-${i}`}>
                  <StockPartCombobox
                    value={p}
                    onChange={(picked) => pickItem(i, picked)}
                    testid={`req-picker-${i}`}
                  />
                </div>
                <Input type="number" min={1} value={p.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="col-span-2 rounded-none border-neutral-400 h-9 text-right font-mono" data-testid={`req-item-qty-${i}`} />
                <Select value={p.kind} onValueChange={(v) => setItem(i, "kind", v)}>
                  <SelectTrigger data-testid={`req-item-kind-${i}`} className="col-span-2 rounded-none border-neutral-400 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="rb">RB</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={p.chargeable ? "yes" : "no"}
                  onValueChange={(v) => setItem(i, "chargeable", v === "yes")}
                >
                  <SelectTrigger
                    data-testid={`req-item-chargeable-${i}`}
                    className={`col-span-2 rounded-none border-neutral-400 h-9 ${p.chargeable ? "bg-amber-50 border-amber-500 text-amber-900" : ""}`}
                  ><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Notes" value={p.notes} onChange={(e) => setItem(i, "notes", e.target.value)} className="col-span-1 rounded-none border-neutral-400 h-9" />
                <button type="button" onClick={() => removeItem(i)} className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600" aria-label="Remove line">
                  <X size={14} weight="bold" />
                </button>
              </div>
            ))}
            <p className="text-[10px] text-neutral-500 font-mono">
              Chargeable = Yes flags the part as billable to the customer / work order. Kept on the issued voucher for reference.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none border-neutral-400" data-testid="req-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy || lockStatus.blocked} data-testid="req-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? "Saving…" : "Submit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ request, currentUser, onClose, onSaved }) {
  const [rows, setRows] = useState(
    request.items.map((it) => ({
      ...it,
      approved_qty: it.quantity, // pre-tick full approval
      reason: "",
      serials: Array.isArray(it.serials) ? it.serials : [],
    })),
  );
  const [decisionNotes, setDecisionNotes] = useState("");
  const [availability, setAvailability] = useState({});
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState(false);
  const [lockStatus, setLockStatus] = useState({ blocked: false });
  const isManager = currentUser?.role === "manager";

  // Look up on-hand qty for each unique stock_code so storekeeper sees availability inline.
  useEffect(() => {
    let alive = true;
    const codes = Array.from(new Set(rows.map((r) => r.stock_code).filter(Boolean)));
    if (codes.length === 0) return;
    (async () => {
      try {
        const results = await Promise.all(codes.map((code) =>
          api.get("/stock", { params: { q: code, limit: 5 } }).then((res) => ({
            code, on_hand_qty: (res.data.find((s) => s.item === code) || {}).on_hand_qty ?? null,
          })),
        ));
        if (!alive) return;
        const map = {};
        results.forEach((r) => { map[r.code] = r.on_hand_qty; });
        setAvailability(map);
      } catch (err) {
        // Availability is a hint, not a blocker — log so the operator can
        // still see the failure in devtools if something's wrong with the API.
        // eslint-disable-next-line no-console
        console.warn("Availability lookup failed (ignored):", err?.message || err);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id]);

  const setRow = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${request.id}/decision`, {
        items: rows.map((r) => ({
          name: r.name,
          quantity: r.quantity,
          approved_qty: Math.max(0, Math.min(Number(r.approved_qty) || 0, r.quantity)),
          reason: r.reason || null,
          serials: (r.serials || []).map((s) => String(s).trim()).filter(Boolean),
        })),
        decision_notes: decisionNotes,
        override: isManager && override,
      });
      toast.success("Decision recorded");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Feed the lock/quota banner with the parts that WILL be issued (approved_qty > 0).
  const approvedForBanner = rows
    .filter((r) => Number(r.approved_qty) > 0)
    .map((r) => ({
      name: r.name,
      stock_code: r.stock_code || null,
      quantity: Math.max(0, Math.min(Number(r.approved_qty) || 0, r.quantity)),
    }));

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <TrayArrowDown size={22} weight="duotone" /> Review {request.request_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="label-eyebrow">Engineer</span> <div className="mt-0.5 font-medium">{request.for_engineer_name}</div></div>
            <div><span className="label-eyebrow">Region / WO</span> <div className="mt-0.5 font-mono text-xs">{request.region}{request.work_order_id ? ` · ${request.work_order_id}` : ""}</div></div>
            {request.customer_name && (
              <div className="col-span-2"><span className="label-eyebrow">Customer</span> <div className="mt-0.5 font-medium">{request.customer_name}</div></div>
            )}
          </div>
          {request.notes && (
            <div className="text-xs text-neutral-600 border border-neutral-200 bg-neutral-50 px-3 py-2">
              <span className="label-eyebrow">Requester note</span>
              <div className="mt-1">{request.notes}</div>
            </div>
          )}
          <LockStatusBanner
            engineerId={request.for_engineer_id}
            parts={approvedForBanner}
            isManager={isManager}
            override={override}
            onOverrideChange={setOverride}
            onStatusChange={setLockStatus}
            testidPrefix="decision-lock"
          />
          {isManager && (
            <label
              className={`flex items-start gap-3 border-2 px-4 py-3 cursor-pointer select-none transition-colors ${
                override
                  ? "border-amber-500 bg-amber-50"
                  : "border-neutral-300 bg-white hover:border-neutral-900"
              }`}
              data-testid="manager-override-panel"
            >
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amber-600 cursor-pointer"
                data-testid="manager-override-toggle"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-amber-500 text-white">
                    Manager Only
                  </span>
                  <span className={`font-medium text-sm ${override ? "text-amber-900" : "text-neutral-900"}`}>
                    Push through — bypass quota &amp; return-lock for this REQ
                  </span>
                </div>
                <div className="text-[11px] text-neutral-600 mt-1">
                  {override
                    ? "🔓 ACTIVE — This approval will be logged as a Manager Override in the audit log."
                    : "Flip on when a high-value REQ needs to go through despite quota caps or unreturned parts. Line-level lock warnings above will still show, but the save will proceed."}
                </div>
              </div>
            </label>
          )}
          <div className="border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="decision-rows">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr className="text-left">
                  <th className="px-3 py-2 label-eyebrow">Part</th>
                  <th className="px-3 py-2 label-eyebrow text-right">Requested</th>
                  <th className="px-3 py-2 label-eyebrow text-right">On Hand</th>
                  <th className="px-3 py-2 label-eyebrow text-right">Approve Qty</th>
                  <th className="px-3 py-2 label-eyebrow">Reason (if reject/partial)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const onHand = r.stock_code ? availability[r.stock_code] : null;
                  const insufficient = onHand != null && Number(r.approved_qty) > onHand;
                  return (
                    <Fragment key={i}>
                    <tr className="border-b border-neutral-100 last:border-0" data-testid={`decision-row-${i}`}>
                      <td className="px-3 py-2">
                        <div className="text-xs font-medium">{r.name}</div>
                        <div className="flex gap-1 mt-0.5 flex-wrap">
                          {r.chargeable && <span className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-900 px-1 py-0.5">Chargeable</span>}
                          {r.group && <span className="text-[9px] font-mono uppercase tracking-wider bg-neutral-100 px-1 py-0.5">{r.group}</span>}
                          {r.stock_code == null && <span className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 px-1 py-0.5">custom</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.quantity}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {r.stock_code == null
                          ? <span className="text-neutral-400">n/a</span>
                          : onHand == null
                            ? <span className="text-neutral-400">…</span>
                            : (
                              <span className={onHand === 0 ? "text-red-700 font-semibold" : "text-neutral-700"}>{onHand}</span>
                            )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number" min={0} max={r.quantity}
                          value={r.approved_qty}
                          onChange={(e) => setRow(i, "approved_qty", e.target.value)}
                          className={`w-20 ml-auto text-right h-8 rounded-none border-neutral-400 font-mono ${insufficient ? "border-red-500" : ""}`}
                          data-testid={`decision-approved-${i}`}
                        />
                        {insufficient && (
                          <div className="text-[10px] text-red-700 mt-0.5 flex items-center justify-end gap-1"><Warning size={10} weight="bold" />exceeds on-hand</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={r.reason}
                          onChange={(e) => setRow(i, "reason", e.target.value)}
                          placeholder={Number(r.approved_qty) < r.quantity ? "e.g. out of stock" : ""}
                          className="rounded-none border-neutral-400 h-8"
                          data-testid={`decision-reason-${i}`}
                        />
                      </td>
                    </tr>
                    {Number(r.approved_qty) > 0 && (
                      <tr className="border-b border-neutral-100 last:border-0 bg-neutral-50/60" data-testid={`decision-serial-row-${i}`}>
                        <td colSpan={5} className="px-3 py-2">
                          <SerialEditor
                            serials={r.serials || []}
                            onChange={(next) => setRow(i, "serials", next)}
                            testidPrefix={`decision-serial-${i}`}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Decision notes (optional)</Label>
            <Textarea rows={2} value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} className="rounded-none border-neutral-400" data-testid="decision-notes" />
          </div>
          <div className="text-xs text-neutral-500 flex items-start gap-2">
            <CheckCircle size={14} weight="duotone" className="mt-0.5 shrink-0 text-emerald-700" />
            Approved lines will automatically create an <b>IV</b> voucher and deduct physical stock. Lines set to 0 are treated as <b>rejected</b>.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            variant="outline"
            onClick={() => { setRows(rows.map((r) => ({ ...r, approved_qty: 0 }))); }}
            data-testid="decision-reject-all"
            className="rounded-none border-red-600 text-red-700 hover:bg-red-600 hover:text-white"
          >
            <XCircle size={14} weight="bold" className="mr-1" /> Reject all
          </Button>
          <Button
            variant="outline"
            onClick={() => { setRows(rows.map((r) => ({ ...r, approved_qty: r.quantity }))); }}
            data-testid="decision-approve-all"
            className="rounded-none border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white"
          >
            <CheckCircle size={14} weight="bold" className="mr-1" /> Approve all
          </Button>
          <Button onClick={submit} disabled={busy || lockStatus.blocked} data-testid="decision-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? "Saving…" : "Save decision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function VerifyDialog({ request, onClose, onSaved }) {
  const [mode, setMode] = useState("approve"); // "approve" | "reject"
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = reason.trim();
    if (mode === "reject" && !trimmed) {
      toast.error("A reason is required when rejecting at verify step");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/requests/${request.id}/verify`, {
        approve: mode === "approve",
        reason: trimmed || null,
      });
      toast.success(mode === "approve" ? "Request verified — Storekeeper can now release" : "Request rejected at verify step");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-neutral-900" data-testid="verify-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <CheckCircle size={22} weight="duotone" className="text-amber-500" />
            Verify {request.request_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="label-eyebrow">Engineer</span> <div className="mt-0.5 font-medium">{request.for_engineer_name}</div></div>
            <div><span className="label-eyebrow">Region / WO</span> <div className="mt-0.5 font-mono text-xs">{request.region}{request.work_order_id ? ` · ${request.work_order_id}` : ""}</div></div>
            {request.customer_name && (
              <div className="col-span-2"><span className="label-eyebrow">Customer</span> <div className="mt-0.5 font-medium">{request.customer_name}</div></div>
            )}
          </div>
          <div className="border border-neutral-200 bg-white px-3 py-2">
            <div className="label-eyebrow mb-1">Requested Parts</div>
            <ul className="text-xs space-y-0.5">
              {(request.items || []).map((it, i) => (
                <li key={i}>
                  <span className="font-medium">{it.name}</span>
                  <span className="text-neutral-500"> × {it.quantity}</span>
                  {it.chargeable && <span className="ml-2 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-900">Chargeable</span>}
                </li>
              ))}
            </ul>
          </div>
          {request.notes && (
            <div className="text-xs text-neutral-600 border border-neutral-200 bg-neutral-50 px-3 py-2">
              <span className="label-eyebrow">Requester note</span>
              <div className="mt-1">{request.notes}</div>
            </div>
          )}
          <div className="border-2 border-neutral-300 p-3 space-y-2">
            <div className="label-eyebrow">Verify Decision</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("approve")}
                data-testid="verify-mode-approve"
                className={`h-11 border-2 text-sm font-medium transition-colors ${
                  mode === "approve"
                    ? "border-amber-500 bg-amber-500 text-white"
                    : "border-neutral-300 bg-white hover:border-amber-500"
                }`}
              >
                ✓ Approve — move to Yellow
              </button>
              <button
                type="button"
                onClick={() => setMode("reject")}
                data-testid="verify-mode-reject"
                className={`h-11 border-2 text-sm font-medium transition-colors ${
                  mode === "reject"
                    ? "border-red-600 bg-red-600 text-white"
                    : "border-neutral-300 bg-white hover:border-red-600"
                }`}
              >
                ✗ Reject with reason
              </button>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">
                {mode === "reject" ? "Rejection reason (required)" : "Verify note (optional)"}
              </Label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={mode === "reject" ? "e.g. Duplicate REQ, wrong engineer, WO not approved…" : "Any note the storekeeper should see"}
                className="rounded-none border-neutral-400"
                data-testid="verify-reason"
              />
            </div>
          </div>
          <div className="text-[11px] text-neutral-500 leading-relaxed">
            Approving moves this REQ from <b>grey (Pending)</b> to <b>yellow (Verified)</b>. The Storekeeper (or Manager) will then release it (yellow → green). Rejecting closes the REQ immediately.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="verify-submit"
            className={`rounded-none text-white disabled:opacity-40 ${
              mode === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-amber-500 hover:bg-amber-600"
            }`}
          >
            {busy ? "Saving…" : mode === "reject" ? "Reject REQ" : "Verify — send to Storekeeper"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// --------------------------------------------------------------------------- //
// Walk-in Sale Dialog — counter cash sale to a walk-in customer.
// Manager / Supervisor only. One-step: pick customer + parts + region, we
// deduct main stock immediately and open the IV PDF for printing.
// --------------------------------------------------------------------------- //
function WalkinSaleDialog({ open, onClose, regions, customers, onSaved }) {
  const [customerId, setCustomerId] = useState("");
  const [region, setRegion] = useState("");
  const [workOrderId, setWorkOrderId] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentRef, setPaymentRef] = useState("");
  const [items, setItems] = useState([
    { name: "", quantity: 1, stock_code: null, group: null, uom: null, unit_price: 0 },
  ]);
  const [priceMap, setPriceMap] = useState({}); // stock_code -> unit_cost from master
  const [busy, setBusy] = useState(false);

  // Reset the form every time the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setCustomerId("");
      setRegion("");
      setWorkOrderId("");
      setNotes("");
      setPaymentMethod("cash");
      setPaymentRef("");
      setItems([{ name: "", quantity: 1, stock_code: null, group: null, uom: null, unit_price: 0 }]);
    }
  }, [open]);

  // Pull unit_cost from the master list for every picked stock_code so we can
  // quote the customer live. Only manager/supervisor/finance get this field
  // back from the API — the dialog is gated to mgr/sup so we're covered.
  useEffect(() => {
    const codes = Array.from(new Set(items.map((r) => r.stock_code).filter(Boolean)));
    const missing = codes.filter((c) => priceMap[c] === undefined);
    if (missing.length === 0) return;
    let alive = true;
    (async () => {
      const updates = {};
      await Promise.all(missing.map(async (code) => {
        try {
          const res = await api.get("/stock", { params: { q: code, limit: 5 } });
          const match = (res.data || []).find((s) => s.item === code);
          updates[code] = match?.unit_cost ?? 0;
        } catch { updates[code] = 0; }
      }));
      if (alive) setPriceMap((prev) => ({ ...prev, ...updates }));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((r) => r.stock_code).join("|")]);

  // Auto-fill unit_price when a stock item is picked (manager can still edit).
  useEffect(() => {
    setItems((rows) => rows.map((r) => {
      if (r.stock_code && (r.unit_price == null || r.unit_price === 0)) {
        const p = priceMap[r.stock_code];
        if (p != null && p > 0) return { ...r, unit_price: p };
      }
      return r;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceMap]);

  const setItem = (i, k, v) => setItems(items.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const pickItem = (i, picked) => setItems(items.map((p, idx) => idx === i ? {
    ...p,
    name: picked.name,
    stock_code: picked.stock_code || null,
    group: picked.group || null,
    uom: picked.uom || null,
    unit_price: picked.stock_code && priceMap[picked.stock_code] != null ? priceMap[picked.stock_code] : (p.unit_price || 0),
  } : p));
  const addItem = () => setItems([...items, { name: "", quantity: 1, stock_code: null, group: null, uom: null, unit_price: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totalAmount = items.reduce((sum, r) => {
    const qty = Number(r.quantity) || 0;
    const price = Number(r.unit_price) || 0;
    return sum + qty * price;
  }, 0);

  const submit = async () => {
    if (!customerId) return toast.error("Select a customer");
    if (!region) return toast.error("Select a region");
    const cleaned = items
      .filter((p) => (p.name || "").trim())
      .map((p) => ({
        name: p.name.trim(),
        quantity: Math.max(1, Number(p.quantity) || 1),
        stock_code: p.stock_code || null,
        group: p.group || null,
        uom: p.uom || null,
        unit_price: Math.max(0, Number(p.unit_price) || 0),
        notes: "",
      }));
    if (cleaned.length === 0) return toast.error("Add at least one part");
    setBusy(true);
    try {
      const res = await api.post("/records/walkin-sale", {
        customer_id: customerId,
        region,
        work_order_id: workOrderId.trim() || null,
        parts: cleaned,
        payment_method: paymentMethod,
        payment_ref: paymentRef.trim() || null,
        notes: notes || "",
      });
      toast.success(`Walk-in sale ${res.data.receipt_no} issued`);
      // Open the printable IV PDF in a new tab so the manager can print it.
      window.open(`${API_BASE}/records/${res.data.id}/receipt.pdf`, "_blank", "noopener");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-emerald-700 max-h-[90vh] overflow-y-auto" data-testid="walkin-sale-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2 text-emerald-800">
            <ShoppingCart size={24} weight="duotone" /> Walk-in Sale
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-emerald-700 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            <b>Cash counter sale.</b> Every line is auto-flagged chargeable, stock deducts from main immediately, and an <b>IV</b> receipt prints on save. No engineer bucket involved.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Customer <span className="text-red-600 normal-case">*</span></Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="walkin-customer" className="rounded-none border-neutral-400"><SelectValue placeholder="Pick walk-in customer" /></SelectTrigger>
                <SelectContent>
                  {customers.length === 0
                    ? (
                      <div className="px-2 py-3 text-xs text-neutral-500 max-w-xs">
                        No walk-in customers yet.<br />
                        Go to <b>Customers</b> → <b>Add customer</b> → select <b>Walk-in</b> type first.
                      </div>
                    )
                    : customers.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`walkin-customer-${c.id}`}>{c.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Region <span className="text-red-600 normal-case">*</span></Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="walkin-region" className="rounded-none border-neutral-400"><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Work Order / Ref (optional)</Label>
              <Input value={workOrderId} onChange={(e) => setWorkOrderId(e.target.value)} className="rounded-none border-neutral-400 font-mono" data-testid="walkin-wo" placeholder="e.g. CASH-2026-001" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none border-neutral-400" data-testid="walkin-notes" placeholder="Optional" />
            </div>
          </div>

          {/* Payment method — finance reconciliation lives on this row */}
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Payment method <span className="text-red-600 normal-case">*</span></Label>
            <div className="grid grid-cols-4 gap-2" data-testid="walkin-payment-methods">
              {[
                { key: "cash", label: "Cash" },
                { key: "cheque", label: "Cheque" },
                { key: "card", label: "Card" },
                { key: "other", label: "Other" },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setPaymentMethod(m.key)}
                  data-testid={`walkin-pay-${m.key}`}
                  className={`h-10 border-2 text-sm font-medium transition-colors ${
                    paymentMethod === m.key
                      ? "border-emerald-700 bg-emerald-700 text-white"
                      : "border-neutral-300 bg-white hover:border-emerald-700"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {paymentMethod !== "cash" && (
              <Input
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                className="rounded-none border-neutral-400 font-mono mt-2"
                data-testid="walkin-payment-ref"
                placeholder={
                  paymentMethod === "cheque" ? "Cheque number"
                    : paymentMethod === "card" ? "Card auth / ref"
                    : "Reference"
                }
              />
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Parts to sell</Label>
              <button type="button" onClick={addItem} data-testid="walkin-add-item-btn" className="text-xs font-medium underline underline-offset-4 hover:text-neutral-500">+ Add part</button>
            </div>
            <div className="hidden md:grid grid-cols-12 gap-2 px-1 text-[10px] font-mono uppercase tracking-wider text-neutral-500">
              <div className="col-span-5">Part</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Unit Price (RM)</div>
              <div className="col-span-2 text-right">Line Total</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((p, i) => {
              const qty = Number(p.quantity) || 0;
              const price = Number(p.unit_price) || 0;
              const lineTotal = qty * price;
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`walkin-row-${i}`}>
                  <div className="col-span-5">
                    <StockPartCombobox
                      value={p}
                      onChange={(picked) => pickItem(i, picked)}
                      testid={`walkin-picker-${i}`}
                    />
                  </div>
                  <Input
                    type="number" min={1} value={p.quantity}
                    onChange={(e) => setItem(i, "quantity", e.target.value)}
                    className="col-span-2 rounded-none border-neutral-400 h-9 text-right font-mono"
                    data-testid={`walkin-qty-${i}`}
                  />
                  <Input
                    type="number" min={0} step="0.01" value={p.unit_price}
                    onChange={(e) => setItem(i, "unit_price", e.target.value)}
                    className="col-span-2 rounded-none border-neutral-400 h-9 text-right font-mono"
                    data-testid={`walkin-price-${i}`}
                    placeholder="0.00"
                  />
                  <div className="col-span-2 text-right font-mono text-sm" data-testid={`walkin-linetotal-${i}`}>
                    {lineTotal > 0 ? `RM ${lineTotal.toFixed(2)}` : <span className="text-neutral-400">—</span>}
                  </div>
                  <button type="button" onClick={() => removeItem(i)} className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600" aria-label="Remove line">
                    <X size={14} weight="bold" />
                  </button>
                </div>
              );
            })}
            <p className="text-[10px] text-neutral-500 font-mono">
              Prices auto-fill from the Master Price List when you pick a stock item — you can override per line before saving.
            </p>
          </div>

          <div className="flex items-center justify-between border-t-2 border-emerald-700 pt-3">
            <div className="label-eyebrow">Grand total</div>
            <div className="text-2xl font-display tracking-tight text-emerald-800" data-testid="walkin-grand-total">
              RM {totalAmount.toFixed(2)}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="walkin-submit"
            className="rounded-none bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Saving…" : "Issue Sale + Print IV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------------
// Admin-only edit dialog. Allowed while REQ status ∈ {pending, verified}.
// Lets Admin patch line items (name/qty/kind/chargeable/notes) plus a couple
// of metadata fields before the storekeeper releases and the IV is issued.
// -------------------------------------------------------------------------
function AdminEditRequestDialog({ request, customers, onClose, onSaved }) {
  const [items, setItems] = useState(() =>
    (request.items || []).map((it) => ({
      name: it.name || "", quantity: it.quantity || 1,
      kind: it.kind || "new", chargeable: !!it.chargeable,
      stock_code: it.stock_code || null, group: it.group || null,
      uom: it.uom || null, notes: it.notes || "",
    })),
  );
  const [customerId, setCustomerId] = useState(request.customer_id || "");
  const [workOrder, setWorkOrder] = useState(request.work_order_id || "");
  const [site, setSite] = useState(request.site || "");
  const [notes, setNotes] = useState(request.notes || "");
  const [busy, setBusy] = useState(false);

  const setItem = (i, key, val) => { const n = items.slice(); n[i] = { ...n[i], [key]: val }; setItems(n); };
  const pickItem = (i, picked) => setItems(items.map((row, idx) => (idx === i ? {
    ...row, name: picked.name || "", stock_code: picked.stock_code || null,
    group: picked.group || null, uom: picked.uom || null,
  } : row)));
  const addRow = () => setItems([...items, { name: "", quantity: 1, kind: "new", chargeable: false, stock_code: null, group: null, uom: null, notes: "" }]);
  const removeRow = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);

  const submit = async () => {
    const clean = items.filter((p) => (p.name || "").trim() && Number(p.quantity) > 0);
    if (!clean.length) return toast.error("At least one line is required");
    setBusy(true);
    try {
      const payload = {
        items: clean.map((p) => ({
          name: p.name.trim(),
          quantity: Math.max(1, Number(p.quantity) || 1),
          kind: p.kind || "new",
          chargeable: !!p.chargeable,
          stock_code: p.stock_code || null,
          group: p.group || null,
          uom: p.uom || null,
          notes: p.notes || "",
        })),
        customer_id: customerId || null,
        work_order_id: workOrder || null,
        site: site || null,
        notes,
      };
      await api.patch(`/requests/${request.id}`, payload);
      toast.success(`${request.request_no} updated`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[92vh] overflow-y-auto" data-testid="admin-edit-req-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Admin Edit · {request.request_no}
            <span className="ml-2 text-sm font-mono uppercase tracking-wider text-neutral-500 font-normal">
              ({request.status})
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-neutral-600 bg-amber-50 border border-amber-200 px-3 py-2">
            Admin override — you can change lines, quantities, kind (New / RB) and chargeable flags before the storekeeper issues the IV. All edits are audit-logged.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Customer</Label>
              <Select value={customerId || " "} onValueChange={(v) => setCustomerId(v === " " ? "" : v)}>
                <SelectTrigger data-testid="admin-edit-customer" className="h-10 rounded-none border-neutral-400"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">— none —</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Work Order</Label>
              <Input value={workOrder} onChange={(e) => setWorkOrder(e.target.value)}
                placeholder="Optional" className="h-10 rounded-none border-neutral-400" data-testid="admin-edit-wo" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Line Items *</Label>
              <Button onClick={addRow} size="sm" variant="outline"
                className="h-7 rounded-none border-neutral-400" data-testid="admin-edit-add-line">
                <Plus size={12} weight="bold" className="mr-1" /> Add line
              </Button>
            </div>
            {items.map((p, i) => (
              <div key={i} className="border border-neutral-200 p-2 space-y-2" data-testid={`admin-edit-row-${i}`}>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-6"><StockPartCombobox value={p} onChange={(pk) => pickItem(i, pk)} testid={`admin-edit-picker-${i}`} /></div>
                  <Input type="number" min={1} value={p.quantity}
                    onChange={(e) => setItem(i, "quantity", e.target.value)}
                    className="col-span-2 h-9 rounded-none border-neutral-400 text-right font-mono"
                    data-testid={`admin-edit-qty-${i}`} placeholder="Qty" />
                  <Select value={p.kind} onValueChange={(v) => setItem(i, "kind", v)}>
                    <SelectTrigger data-testid={`admin-edit-kind-${i}`} className="col-span-2 h-9 rounded-none border-neutral-400 font-mono text-xs uppercase"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">NEW</SelectItem>
                      <SelectItem value="rb">RB</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="col-span-1 flex items-center justify-center gap-1 text-[10px] font-mono uppercase tracking-wider">
                    <input type="checkbox" checked={p.chargeable}
                      onChange={(e) => setItem(i, "chargeable", e.target.checked)}
                      className="accent-amber-600" data-testid={`admin-edit-chargeable-${i}`} />
                    Chg
                  </label>
                  <button type="button" onClick={() => removeRow(i)} disabled={items.length === 1}
                    className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600 disabled:opacity-40"
                    aria-label="Remove line" data-testid={`admin-edit-remove-${i}`}>
                    <X size={13} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="rounded-none border-neutral-400" data-testid="admin-edit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="admin-edit-save"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
